import { ContactConflictResolver } from "@/modules/lead-page/components/contact-conflicts/resolver";
import { Stack, useTheme } from "@mui/material";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { useParams, useSearchParams } from "react-router-dom";
import useLeadPageDataFetching from "@/modules/lead-page/hooks/lead-page-data-fetching.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useState, useEffect } from "react";
import { LeadPageTab } from "@/modules/lead-page/new/constants";
import { MobileLayout } from "@/modules/lead-page/new/components/mobile";
import { DesktopLayout } from "@/modules/lead-page/new/components/desktop";
import { useReviewsTabVisibility } from "@/modules/lead-page/new/hooks/use-reviews-tab-visibility.hook";
import { useLeadPageOnCallSse } from "@/modules/lead-page/new/hooks/use-lead-page-oncall-sse.hook";
import { useNawyNowSse } from "@/modules/nawy-now/hooks/use-nawy-now-sse.hook";
import { useAnalyticsContext } from "@/modules/analytics/context/analytics.context";
import { MixpanelTrackingUtil } from "@/modules/analytics/utils";
import NotFound from "@/pages/404";

const LeadPage = () => {
  const { lead_id } = useParams<{ lead_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasQRCode = searchParams.get("hasQRCode");
  const { isMobile } = useScreenSize();
  const theme = useTheme();

  const tabFromUrl = searchParams.get("tab") as LeadPageTab | null;
  const initialTab =
    tabFromUrl && Object.values(LeadPageTab).includes(tabFromUrl)
      ? tabFromUrl
      : LeadPageTab.ACTIVITY;

  const [activeTab, setActiveTab] = useState<LeadPageTab>(initialTab);

  useEffect(() => {
    const currentTab = searchParams.get("tab");
    const reviewId = searchParams.get("reviewId");
    const activityId = searchParams.get("activityId");

    if (activeTab !== currentTab) {
      if (activeTab === LeadPageTab.ACTIVITY && !reviewId && !activityId) {
        searchParams.delete("tab");
        searchParams.delete("reviewId");
        searchParams.delete("trackUId");
        searchParams.delete("activityId");
        searchParams.delete("timestamp");
        setSearchParams(searchParams, { replace: true });
      } else {
        searchParams.set("tab", activeTab);
        if (activeTab !== LeadPageTab.REVIEWS) searchParams.delete("reviewId");
        if (activeTab !== LeadPageTab.DEVELOPER_TRACKS)
          searchParams.delete("trackUId");
        if (activeTab !== LeadPageTab.ACTIVITY) {
          searchParams.delete("activityId");
          searchParams.delete("timestamp");
        }
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [activeTab, searchParams, setSearchParams]);

  const [isHeaderCollapsed] = useState(false);

  // Mobile bottom sheet states
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const [isNextActionsOpen, setIsNextActionsOpen] = useState(false);
  const [isRecommendationsOpen, setIsRecommendationsOpen] = useState(false);
  const [isActivitiesOpen, setIsActivitiesOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isInterestsOpen, setIsInterestsOpen] = useState(false);
  const [isDeveloperTracksOpen, setIsDeveloperTracksOpen] = useState(false);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const [isNawyNowOpen, setIsNawyNowOpen] = useState(false);
  const [isChatsAndCallsOpen, setIsChatsAndCallsOpen] = useState(false);
  const { analytics, userInfo } = useAnalyticsContext();

  useEffect(() => {
    if (isMobile && tabFromUrl === LeadPageTab.REVIEWS) {
      setIsReviewsOpen(true);
    }
    // §Agent-link: workflow-manager task notifications deep-link ?tab=nawy_now(&stepUid=…). On mobile
    // the Nawy Now board is a full-screen sheet, not a tab — the link must open it (REVIEWS pattern);
    // the board itself then consumes stepUid and reveals the step.
    if (isMobile && tabFromUrl === LeadPageTab.NAWY_NOW) {
      setIsNawyNowOpen(true);
    }
  }, [isMobile, tabFromUrl]);

  // Sidebar modal state
  const [sidebarModalOpen, setSidebarModalOpen] = useState(false);

  const { leadData, isLoading, stagesData, isStagesLoading, leadIsError } =
    useLeadPageDataFetching(lead_id, undefined, undefined, !!hasQRCode);

  const isLeadArchived = leadData?.data?.lead?.archived ?? false;

  // Live in-call indicator for the journey being viewed (covers desktop + mobile layouts).
  useLeadPageOnCallSse({
    orgUid: leadData?.data?.lead?.organization?.uid,
    journeyUid: leadData?.data?.lead?.journey_uid,
  });

  // §Realtime: workflow-manager case events → nawy-now tree refetch. Mounted here (not inside the
  // tab) so the above-the-tabs banners stay fresh even while the Nawy Now tab is closed.
  // §Journey-uid: the top-level lead-page response IS the journey, so its own `uid` is the journey uid
  // — the SAME identifier every nawy-now query keys on (see LeadNawyNowTab's journeyUid). NOT
  // `lead.journey_uid`, which does not exist on this response: reading it left the subscription
  // permanently disabled (enabled: Boolean(orgUid && journeyUid)) — caught by browser verification.
  useNawyNowSse({
    orgUid: leadData?.data?.lead?.organization?.uid,
    journeyUid: leadData?.data?.uid,
  });

  // Check if Reviews tab should be visible based on rank, hierarchy, and permissions
  // Also handles filtering tabs and switching active tab if needed
  const leadUid = leadData?.data?.lead?.uid;
  const leadType = leadData?.data?.lead?.leadType;

  const {
    filteredTabs,
    isReviewsTabVisible,
    isDeveloperTracksTabVisible,
    isChatsAndCallsTabVisible,
  } = useReviewsTabVisibility({
    activeTab,
    setActiveTab,
    leadUid,
    leadType,
  });

  // Remove hasQRCode from URL after page loads
  useEffect(() => {
    if (hasQRCode) {
      searchParams.delete("hasQRCode");
      setSearchParams(searchParams, { replace: true });
    }
  }, [hasQRCode, searchParams, setSearchParams]);

  // Handle person button click in compact layout
  const handlePersonClick = () => {
    setSidebarModalOpen(true);
  };

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    if (tabId === LeadPageTab.CHATS_AND_CALLS) {
      MixpanelTrackingUtil.trackChatsCallsTabClick(
        analytics,
        "desktop_tab",
        userInfo,
      );
    }
    setActiveTab(tabId as LeadPageTab);
  };

  // Mobile "Chats & calls" action card opens the same surface as a full-page modal
  const handleChatsAndCallsClick = () => {
    MixpanelTrackingUtil.trackChatsCallsTabClick(
      analytics,
      "mobile_card",
      userInfo,
    );
    setIsChatsAndCallsOpen(true);
  };

  // Handle navigation to a developer track from activity timeline
  const handleNavigateToTrack = (trackUId: string) => {
    searchParams.set("tab", LeadPageTab.DEVELOPER_TRACKS);
    searchParams.set("trackUId", trackUId);
    setSearchParams(searchParams, { replace: true });
    setActiveTab(LeadPageTab.DEVELOPER_TRACKS);
  };

  // Mobile action handlers
  const handleTasksClick = () => setIsTasksOpen(true);
  const handleNextActionsClick = () => setIsNextActionsOpen(true);
  const handleRecommendationsClick = () => setIsRecommendationsOpen(true);
  const handleActivitiesClick = () => setIsActivitiesOpen(true);
  const handleSalesClick = () => setIsSalesOpen(true);
  const handlePropertiesClick = () => setIsPropertiesOpen(true);
  const handleInterestsClick = () => setIsInterestsOpen(true);
  const handleDeveloperTracksClick = () => setIsDeveloperTracksOpen(true);

  // Mobile close handlers
  const handleTasksClose = () => setIsTasksOpen(false);
  const handleNextActionsClose = () => setIsNextActionsOpen(false);
  const handleRecommendationsClose = () => setIsRecommendationsOpen(false);
  const handleActivitiesClose = () => setIsActivitiesOpen(false);
  const handleSalesClose = () => setIsSalesOpen(false);
  const handlePropertiesClose = () => setIsPropertiesOpen(false);
  const handleInterestsClose = () => setIsInterestsOpen(false);
  const handleDeveloperTracksClose = () => setIsDeveloperTracksOpen(false);
  const handleRatingsClick = () => setIsReviewsOpen(true);
  const handleRatingsClose = () => setIsReviewsOpen(false);
  const handleNawyNowClick = () => setIsNawyNowOpen(true);
  const handleNawyNowClose = () => setIsNawyNowOpen(false);

  // When the lead fetch fails (e.g. a 403 because the viewer lacks access to this
  // journey), render the 404 "Page Not Found" state instead of an empty lead shell.
  if (leadIsError) {
    return (
      <Stack
        width="100%"
        height="calc(100vh - 60px)"
        alignItems="center"
        justifyContent="center"
        bgcolor={theme.nawy.colors.semantic.layer.secondary}
        data-testid="lead-page-error"
      >
        <NotFound />
      </Stack>
    );
  }

  return (
    <LeadPageContext.Provider
      value={{
        lead: leadData,
        stages: stagesData,
        isStagesLoading: isStagesLoading,
      }}
    >
      <Stack
        direction="column"
        width="100%"
        height={isMobile ? "auto" : "calc(100vh - 60px)"}
        bgcolor={theme.nawy.colors.semantic.layer.secondary}
        minHeight={isMobile ? "calc(100vh - 60px)" : undefined}
        overflow={isMobile ? "visible" : "hidden"}
      >
        {isMobile ? (
          <MobileLayout
            isLoading={isLoading}
            isHeaderCollapsed={isHeaderCollapsed}
            isLeadArchived={isLeadArchived}
            onPersonClick={handlePersonClick}
            sidebarModalOpen={sidebarModalOpen}
            onSidebarModalClose={() => setSidebarModalOpen(false)}
            isTasksOpen={isTasksOpen}
            isNextActionsOpen={isNextActionsOpen}
            isRecommendationsOpen={isRecommendationsOpen}
            isActivitiesOpen={isActivitiesOpen}
            isSalesOpen={isSalesOpen}
            isPropertiesOpen={isPropertiesOpen}
            onTasksClick={handleTasksClick}
            isInterestsOpen={isInterestsOpen}
            onNextActionsClick={handleNextActionsClick}
            onRecommendationsClick={handleRecommendationsClick}
            onActivitiesClick={handleActivitiesClick}
            onSalesClick={handleSalesClick}
            onPropertiesClick={handlePropertiesClick}
            onTasksClose={handleTasksClose}
            onInterestsClick={handleInterestsClick}
            onNextActionsClose={handleNextActionsClose}
            onRecommendationsClose={handleRecommendationsClose}
            onActivitiesClose={handleActivitiesClose}
            onSalesClose={handleSalesClose}
            onPropertiesClose={handlePropertiesClose}
            onInterestsClose={handleInterestsClose}
            isDeveloperTracksOpen={isDeveloperTracksOpen}
            onDeveloperTracksClick={handleDeveloperTracksClick}
            onDeveloperTracksClose={handleDeveloperTracksClose}
            onRatingsClick={handleRatingsClick}
            onRatingsClose={handleRatingsClose}
            isRatingsVisible={isReviewsTabVisible}
            isRatingsOpen={isReviewsOpen}
            isDeveloperTracksVisible={isDeveloperTracksTabVisible ?? false}
            isNawyNowOpen={isNawyNowOpen}
            onNawyNowClick={handleNawyNowClick}
            onNawyNowClose={handleNawyNowClose}
            isChatsAndCallsVisible={isChatsAndCallsTabVisible}
            isChatsAndCallsOpen={isChatsAndCallsOpen}
            onChatsAndCallsClick={handleChatsAndCallsClick}
            onChatsAndCallsClose={() => setIsChatsAndCallsOpen(false)}
          />
        ) : (
          <DesktopLayout
            isLoading={isLoading}
            activeTab={activeTab}
            isLeadArchived={isLeadArchived}
            filteredTabs={filteredTabs}
            onTabChange={handleTabChange}
            onNavigateToTrack={handleNavigateToTrack}
          />
        )}
      </Stack>
      <ContactConflictResolver />
    </LeadPageContext.Provider>
  );
};

export default LeadPage;
